import React, {useEffect, useState} from "react";
import Api from "../../Api";
import {withAuth} from "../../hoc/Auth";
import {Column, Container, Wrapper} from "../../hoc/Layout";
import Chart from "react-google-charts";

import classes from "./styles.module.css";

function formatNumber(num) {
    return new Intl.NumberFormat("ru-RU").format(Math.round(num));
}

function getProgressColor(percent) {
    if (percent >= 100) return "#f44336";
    if (percent >= 90) return "#ff5722";
    if (percent >= 75) return "#ff9800";
    return "#4caf50";
}

const CraftliteLimitPage = withAuth(function ({token}) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;

        async function loadStats() {
            try {
                setLoading(true);
                const result = await new Api({token}).getCraftliteLimitStats();
                if (!cancelled) {
                    setData(result);
                    setError(null);
                }
            } catch (err) {
                if (!cancelled) {
                    setError(err.message || "Failed to load data");
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        loadStats();

        const interval = setInterval(loadStats, 30000);
        return () => {
            cancelled = true;
            clearInterval(interval);
        };
    }, [token]);

    // Pie chart data
    const pieData = data
        ? [
            ["Segment", "Amount"],
            ["Использовано", data.usedRub],
            ["Остаток", data.remainingRub],
        ]
        : null;

    // Bar chart data (merchants)
    const barData =
        data && data.merchants && data.merchants.length > 0
            ? [
                ["Мерчант", "Сумма (RUB)", {role: "style"}],
                ...data.merchants.map((m, i) => {
                    const colors = ["#2196f3", "#4caf50", "#ff9800", "#9c27b0", "#f44336", "#00bcd4"];
                    return [m.merchantName, m.sumRub, colors[i % colors.length]];
                }),
            ]
            : null;

    return (
        <Wrapper>
            <Container style={{justifyContent: 'center'}}>
                <Column width={1180}>
                    <h4 className="mb-3">Дневной лимит Craftlite</h4>

                    {loading && !data ? (
                        <div className={classes.LoadingWrap}>
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    ) : error ? (
                        <div className={classes.ErrorCard}>{error}</div>
                    ) : (
                        <div className={classes.ContentGrid}>
                            {/* Left column — charts & table */}
                            <div className={classes.MainColumn}>
                                {/* Pie Chart */}
                                <div className={classes.Card}>
                                    <div className={classes.Header}>
                                        <h5 className={classes.Title}>Общий лимит</h5>
                                        {data && <span className={classes.DateLabel}>{data.date}</span>}
                                    </div>
                                    <div className={classes.Body}>
                                        {pieData && (
                                            <div className={classes.ChartWrap}>
                                                <Chart
                                                    width="100%"
                                                    height="300px"
                                                    chartType="PieChart"
                                                    data={pieData}
                                                    options={{
                                                        pieHole: 0.5,
                                                        colors: [getProgressColor(data.usedPercent), "#e0e0e0"],
                                                        legend: {position: "bottom", textStyle: {fontSize: 13}},
                                                        chartArea: {width: "90%", height: "80%"},
                                                        pieSliceText: "value",
                                                        tooltip: {text: "both"},
                                                    }}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Bar Chart: by merchant */}
                                {barData && (
                                    <div className={classes.Card}>
                                        <div className={classes.Header}>
                                            <h5 className={classes.Title}>По мерчантам</h5>
                                        </div>
                                        <div className={classes.Body}>
                                            <div className={classes.ChartWrap}>
                                                <Chart
                                                    width="100%"
                                                    height="300px"
                                                    chartType="BarChart"
                                                    data={barData}
                                                    options={{
                                                        legend: "none",
                                                        chartArea: {width: "60%", height: "80%"},
                                                        hAxis: {title: "RUB", minValue: 0},
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Merchants table */}
                                {data && data.merchants && data.merchants.length > 0 && (
                                    <div className={classes.Card}>
                                        <div className={classes.Header}>
                                            <h5 className={classes.Title}>Детализация</h5>
                                        </div>
                                        <div className={`${classes.Body} ${classes.TableBody}`}>
                                            <table className={classes.MerchantTable}>
                                                <thead>
                                                <tr>
                                                    <th>Мерчант</th>
                                                    <th className={classes.AlignRight}>Сумма (RUB)</th>
                                                    <th className={classes.AlignRight}>Транзакции</th>
                                                </tr>
                                                </thead>
                                                <tbody>
                                                {data.merchants.map((m) => (
                                                    <tr key={m.merchantId}>
                                                        <td>{m.merchantName}</td>
                                                        <td className={classes.AlignRight}>{formatNumber(m.sumRub)}</td>
                                                        <td className={classes.AlignRight}>{m.count}</td>
                                                    </tr>
                                                ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Right column — summary */}
                            <div className={classes.SideColumn}>
                                {data && (
                                    <div>
                                        {/* Summary */}
                                        <div className={classes.SummaryCard}>
                                            <h6 className={classes.SummaryTitle}>Общий лимит</h6>

                                            <div className={classes.ProgressWrap}>
                                                <div className={classes.ProgressBar}>
                                                    <div
                                                        className={classes.ProgressFill}
                                                        style={{
                                                            width: Math.min(data.usedPercent, 100) + "%",
                                                            backgroundColor: getProgressColor(data.usedPercent),
                                                        }}
                                                    />
                                                </div>
                                                <div className={classes.ProgressInfo}>
                                                    <span>{data.usedPercent.toFixed(1)}%</span>
                                                    <span>{data.date}</span>
                                                </div>
                                            </div>

                                            <div className={classes.SummaryRow}>
                                                <span className={classes.SummaryLabel}>Лимит</span>
                                                <span className={classes.SummaryValue}>{formatNumber(data.limitRub)} RUB</span>
                                            </div>
                                            <div className={classes.SummaryRow}>
                                                <span className={classes.SummaryLabel}>Использовано</span>
                                                <span className={classes.SummaryValue}>{formatNumber(data.usedRub)} RUB</span>
                                            </div>
                                            <div className={classes.SummaryRow}>
                                                <span className={classes.SummaryLabel}>Остаток</span>
                                                <span className={classes.SummaryValue}>{formatNumber(data.remainingRub)} RUB</span>
                                            </div>

                                            {data.isBlocked ? (
                                                <div className={classes.BlockedBadge}>ЛИМИТ ДОСТИГНУТ</div>
                                            ) : (
                                                <div className={classes.ActiveBadge}>Активен</div>
                                            )}
                                        </div>

                                        {/* Alert thresholds */}
                                        <div className={classes.SummaryCard}>
                                            <h6 className={classes.SummaryTitle}>Пороги оповещений</h6>
                                            <div className={classes.SummaryRow}>
                                                <span className={classes.SummaryLabel}>⚠️ 1 000 000 RUB</span>
                                                <span className={classes.SummaryValue}>{data.usedRub >= 1000000 ? "✅" : "⭕"}</span>
                                            </div>
                                            <div className={classes.SummaryRow}>
                                                <span className={classes.SummaryLabel}>🔴 1 250 000 RUB</span>
                                                <span className={classes.SummaryValue}>{data.usedRub >= 1250000 ? "✅" : "⭕"}</span>
                                            </div>
                                            <div className={classes.SummaryRow}>
                                                <span className={classes.SummaryLabel}>🛑 1 500 000 RUB</span>
                                                <span className={classes.SummaryValue}>{data.usedRub >= 1500000 ? "✅" : "⭕"}</span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </Column>
            </Container>
        </Wrapper>
    );
});

export default CraftliteLimitPage;



// WEBPACK FOOTER //
// ./src/PagesOfAdministrator/CraftliteLimit/index.js